import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFig } from '../session/FigProvider.js';
import type { RoleClaim } from '../core/api/types.js';
import { FullPageSpinner } from '../components/FullPageSpinner.js';

/**
 * The client-side half of the portal / admin boundary.
 *
 * The API is the real boundary and it already refuses correctly - a portal token gets 403 on
 * `/api/agency`, `/api/connections` and `/api/users`, verified live. But `ClientPortalSurfaceTests`
 * polices the SERVER. Nothing stopped this app RENDERING admin chrome to a portal token: the
 * data would never arrive, and what the person would see is a broken admin screen they were
 * never meant to know existed. That was a named open gap; this closes it.
 *
 * It fails CLOSED. An unrecognised role, or no role at all, renders nothing and redirects -
 * there is no "allow by default" branch, deliberately, so adding a fourth role later cannot
 * silently grant it access to a tree nobody listed it for.
 */
export function RequireRole({ allow, children, fallback = '/' }: {
  allow: readonly RoleClaim[];
  children: ReactNode;
  fallback?: string;
}) {
  const { role, ready } = useFig();
  if (!ready) return <FullPageSpinner label="Checking your access" />;
  if (role === null) return <Navigate to="/sign-in" replace />;
  if (!allow.includes(role)) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}

/** The two trees, named once so a route file cannot invent its own list. */
export const AGENCY_ROLES: readonly RoleClaim[] = ['admin', 'individual', 'qareviewer'];
export const PORTAL_ROLES: readonly RoleClaim[] = ['client_portal'];
/** Administration is Agency Admin only - narrower than "any agency role". */
export const ADMIN_ONLY: readonly RoleClaim[] = ['admin'];
